import type { Command } from '../../types/commands'
import { formatCommandOutput, colorize, formatHighlight, formatTechnology } from '../colorScheme'
import { dividers, decorations } from '../asciiArt'

export const aboutCommand: Command = {
name: 'about',
description: 'Learn about Anderson Reinkordt',
aliases: ['whoami', 'info'],
category: 'info',
execute: () => {
const asciiArt = ``

const aboutText = `
${asciiArt}
╔══════════════════════════════════════════════════════════════════╗
║                                                       __         ║
║                    ${formatHighlight('ANDERSON REINKORDT')}              ___( o)>      ║
║        ${formatTechnology('AI Engineer')} | Accessible Intelligence       \\ <_. )       ║
║                                                     \`---'        ║
╚══════════════════════════════════════════════════════════════════╝

╭─ 👤 PROFILE ───────────────────────────────────────────────╮
│                                                            │
│  Software engineer building AI tools and accessible        │
│  technology for underserved communities.                   │
│                                                            │
│                                                            │
│  Founder: can.code Labs                                    │
│  Focus: Building accessible tools through transparent      │
│         open-source development                            │
╰────────────────────────────────────────────────────────────╯

${colorize(dividers.wave, 'brightBlack')}

╭─ 🎯 MISSION ───────────────────────────────────────────────╮
│                                                            │
│  Creating AI systems that prioritize:                     │
│    ${decorations.triangleBullet} Human-centered design                                │
│    ${decorations.triangleBullet} Accessibility for neurodivergent communities         │
│    ${decorations.triangleBullet} Transparency in capabilities and limitations         │
│    ${decorations.triangleBullet} Recognition over correction                          │
│    ${decorations.triangleBullet} Learning in the open                                │
╰────────────────────────────────────────────────────────────╯

${colorize(dividers.circuit, 'brightBlack')}

╭─ 💡 PHILOSOPHY ────────────────────────────────────────────╮
│                                                            │
│  ${colorize('"To teach is to learn"', 'brightYellow')}                                   │
│                                                            │
│  Under-promise, over-deliver. Share what works AND what   │
│  doesn't. Build in public, learn in the open, design      │
│  for inclusion.                                           │
╰────────────────────────────────────────────────────────────╯

${colorize(dividers.stars, 'yellow')}

╭─ 🚀 NOTABLE PROJECTS ──────────────────────────────────────╮
│                                                            │
│  ${decorations.starBullet} YAPS: Voice-to-content creation tool                   │
│  ${decorations.starBullet} jott: Instant thought capture CLI and project mgmt     │
│  ${decorations.starBullet} PRISM: Neurodivergent communication assistance         │
│  ${decorations.starBullet} Ideanas: AI-powered ideas management                   │
│  ${decorations.starBullet} jobsearch-cli: Intelligent job search automation       │
│  ${decorations.starBullet} tts-extension: Accessibility-first browser extension   │
│  ${decorations.starBullet} StoryTimeStar: AI storytelling for children            │
╰────────────────────────────────────────────────────────────╯

╭─ 📊 OPEN-SOURCE GOALS ────────────────────────────────────╮
│                                                            │
│  Actively working towards:                                │
│  • Releasing production-ready CLI tools as open-source    │
│  • Building assistive technology in the open              │
│  • Sharing learnings through transparent development      │
│  • Growing open-source collaboration                      │
╰────────────────────────────────────────────────────────────╯

╭─ 🌱 ORIGIN STORY ─────────────────────────────────────────╮
│                                                            │
│  Before I wrote code, I taught at a farm-based Montessori  │
│  school for nearly a decade. Meeting people where they     │
│  are, breaking complex things into understandable pieces,  │
│  turned out to be exactly what engineering demands.        │
│                                                            │
│  >> Type ${formatHighlight('story')} for the full journey                       │
╰────────────────────────────────────────────────────────────╯

~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~
  >> Type ${formatHighlight('skills')} to see technical expertise
  >> Type ${formatHighlight('projects')} to explore portfolio
  >> Type ${formatHighlight('contact')} to get in touch
~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~
`

    // Apply formatting to the text
    const formattedText = formatCommandOutput(aboutText, {
      highlightTech: true,
      highlightNumbers: true,
      highlightDates: false,
      highlightPaths: false,
      highlightUrls: true,
    })

    return [
      {
        type: 'info',
        content: (
          <div dangerouslySetInnerHTML={{ __html: formattedText }} />
        ),
      },
      {
        type: 'info',
        content: (
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <img
              src="/starfighter.webp"
              alt="Anderson Reinkordt"
              style={{
                maxWidth: '400px',
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                border: '2px solid var(--terminal-glow, #00ff00)',
                boxShadow: '0 0 10px rgba(0, 255, 0, 0.3)'
              }}
            />
          </div>
        ),
      },
    ]
  },
}
